import React, { useEffect } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { useDropzone } from 'react-dropzone';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ImageDeleteButton,
  imageDeleteRevealStyles,
} from '../../ImageDeleteButton';
import { Button } from '../Button';
import { ImageThumbnail } from '../ImageThumbnail';

export interface FileWithPreview extends File {
  preview: string;
}

export interface ImageDropzoneProps {
  files: FileWithPreview[];
  onFileRemove?: (file: FileWithPreview) => void;
  onFilesAccept: (files: File[]) => void;
  setFiles: (files: FileWithPreview[]) => void;
}

const dropzoneStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  minHeight: 144,
  p: 2.5,
  boxSizing: 'border-box',
  border: '1px dashed',
  borderColor: 'outline',
  borderRadius: 1.5,
  bgcolor: 'sunken',
  color: 'text.secondary',
  cursor: 'pointer',
  transition: 'border-color 0.15s ease, background-color 0.15s ease',
  '&:hover': {
    borderColor: 'text.secondary',
    bgcolor: 'action.hover',
  },
};
const dropzoneContentStyles = { alignItems: 'center', textAlign: 'center' };
const promptStyles = { alignItems: 'center' };
const uploadIconStyles = { fontSize: 21 };
const actionRowStyles = { alignItems: 'center' };
const selectButtonStyles = { minHeight: 36, px: 2 };
const formatsStyles = { color: 'text.secondary' };
const thumbnailStyles = imageDeleteRevealStyles;
const thumbsContainerStyles = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: 1.5,
};
const containerStyles = { width: '100%' };
const dropzoneClassProps = { className: 'dropzone' };

const ImageDropzone = React.memo(
  ({ files, onFileRemove, onFilesAccept, setFiles }: ImageDropzoneProps) => {
    const intl = useIntl();
    const { getRootProps, getInputProps } = useDropzone({
      accept: {
        'image/*': ['.jpg', '.jpeg', '.png'],
      },
      onDrop: async (acceptedFiles) => {
        const hashes = files.map(
          (file) => `${file.name}-${file.type}-${file.size}`
        );
        const filteredFiles = acceptedFiles.filter(
          (file) => !hashes.includes(`${file.name}-${file.type}-${file.size}`)
        );
        setFiles([
          ...files,
          ...filteredFiles.map((file) =>
            Object.assign(file, {
              preview: URL.createObjectURL(file),
            })
          ),
        ]);

        await onFilesAccept(filteredFiles);
      },
    });
    const rootProps = getRootProps(dropzoneClassProps);
    const inputProps = getInputProps();

    const handleRemove = (file: FileWithPreview) => {
      setFiles(files.filter((candidate) => candidate !== file));
      onFileRemove?.(file);
    };

    const thumbnails = files.map((file, index) => {
      const handleDelete = () => handleRemove(file);
      const imageProps = {
        onLoad: () => URL.revokeObjectURL(file.preview),
      };

      return (
        <ImageThumbnail
          key={`${file.name}-${file.type}-${file.size}`}
          alt={intl.formatMessage(
            {
              id: 'image.preview.alt',
              defaultMessage: 'Image preview {number}',
            },
            { number: index + 1 }
          )}
          imageProps={imageProps}
          src={file.preview}
          sx={thumbnailStyles}
        >
          <ImageDeleteButton
            offset={2}
            title={intl.formatMessage({
              id: 'image.remove.action',
              defaultMessage: 'Remove image',
            })}
            onDelete={handleDelete}
          />
        </ImageThumbnail>
      );
    });

    useEffect(
      () => () => {
        files.forEach((file) => URL.revokeObjectURL(file.preview));
      },
      [files]
    );

    return (
      <Stack gap={2} sx={containerStyles}>
        <Box sx={dropzoneStyles} {...rootProps}>
          <input {...inputProps} />
          <Stack gap={1.5} sx={dropzoneContentStyles}>
            <Stack direction="row" gap={1} sx={promptStyles}>
              <FileUploadOutlinedIcon sx={uploadIconStyles} />
              <Typography variant="body2">
                <FormattedMessage
                  id="image.dropzone.prompt"
                  defaultMessage="Drag files here or click to select files"
                />
              </Typography>
            </Stack>
            <Stack direction="row" gap={1.5} sx={actionRowStyles}>
              <Button
                tone="neutral"
                type="button"
                sx={selectButtonStyles}
              >
                <FormattedMessage
                  id="image.dropzone.select"
                  defaultMessage="Select files"
                />
              </Button>
              <Typography variant="body2" sx={formatsStyles}>
                <FormattedMessage
                  id="image.dropzone.formats"
                  defaultMessage="JPG, PNG"
                />
              </Typography>
            </Stack>
          </Stack>
        </Box>
        {thumbnails.length > 0 && (
          <Box component="aside" sx={thumbsContainerStyles}>
            {thumbnails}
          </Box>
        )}
      </Stack>
    );
  }
);

ImageDropzone.displayName = 'ImageDropzone';

export { ImageDropzone };
